
import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { useTenant } from './TenantContext';
import { TerminologySettings } from '../types';
import { getGlobalTerminology } from '../services/dataService';

const DEFAULT_TERMS: TerminologySettings = {
  portfolio: 'Portfolio',
  artist: 'Artist',
  artists: 'Artists',
  client: 'Client',
  clients: 'Clients',
  service: 'Service',
  services: 'Services',
  booking: 'Session',
  bookings: 'Sessions',
  project: 'Project',
  projects: 'Projects'
};

const TerminologyContext = createContext<TerminologySettings>(DEFAULT_TERMS);

export const useTerminology = () => useContext(TerminologyContext);

export const TerminologyProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const { tenant } = useTenant();
  const [globalTerms, setGlobalTerms] = useState<TerminologySettings>(DEFAULT_TERMS);

  const fetchGlobal = async () => {
      try {
          const terms = await getGlobalTerminology();
          if (terms && typeof terms === 'object') {
              const merged = { ...DEFAULT_TERMS, ...terms };
              setGlobalTerms(merged);
          }
      } catch(e) {
          console.warn("Failed to fetch global terminology", e);
      }
  };

  useEffect(() => {
      fetchGlobal();
      const handleUpdate = (e: Event) => {
          const customEvent = e as CustomEvent;
          if (customEvent.detail && typeof customEvent.detail === 'object') {
              const updated = { ...DEFAULT_TERMS, ...customEvent.detail };
              setGlobalTerms(updated);
              if (tenant && tenant.config) {
                  tenant.config.terminology = updated;
              }
          } else {
              fetchGlobal();
          }
      };
      window.addEventListener('syncz_terminology_updated', handleUpdate);
      return () => window.removeEventListener('syncz_terminology_updated', handleUpdate);
  }, [tenant]);

  const terms = useMemo(() => {
    let tenantTerms: any = null;
    if (tenant && !tenant.isRoot) {
      if (tenant?.config?.terminology && typeof tenant.config.terminology === 'object') {
        tenantTerms = tenant.config.terminology;
      } else if ((tenant as any)?.terminology) {
        try {
          tenantTerms = typeof (tenant as any).terminology === 'string' 
            ? JSON.parse((tenant as any).terminology) 
            : (tenant as any).terminology;
        } catch (e) {}
      }
    }

    if (tenantTerms && typeof tenantTerms === 'object') {
      return { ...DEFAULT_TERMS, ...globalTerms, ...tenantTerms };
    }
    return { ...DEFAULT_TERMS, ...globalTerms };
  }, [tenant, globalTerms]);

  return (
    <TerminologyContext.Provider value={terms}>
      {children}
    </TerminologyContext.Provider>
  );
};
